import { useEffect, useState } from "react";
import { ChevronLeft, CheckCircle2, XCircle, Pause, Archive } from "lucide-react";
import client, { extractError } from "@/api";

const APPROVAL_LABEL = { DRAFT: "Draft", PENDING_REVIEW: "Pending", APPROVED: "Approved", REJECTED: "Rejected" };
const OPERATIONAL_LABEL = { ACTIVE: "Active", PAUSED: "Paused", ARCHIVED: "Archived", SOLD_OUT: "Sold out", UNAVAILABLE: "Unavailable" };
const FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "PENDING_REVIEW", label: "Pending review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

function RejectDialog({ onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { setError("Reason must be at least 5 characters."); return; }
    setBusy(true); setError("");
    try { await onSubmit({ reason, admin_notes: notes || undefined }); onClose(); }
    catch (err) { setError(extractError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="dialog-backdrop" role="dialog" data-testid="listing-reject-dialog">
      <form className="dialog" onSubmit={submit}>
        <h3>Reject listing</h3>
        <label>Reason (shared with company) *
          <textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
        </label>
        <label>Internal admin notes (private)
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="reject-notes" />
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="dialog-actions">
          <button type="button" className="ghost-button" onClick={onClose} data-testid="reject-cancel">Cancel</button>
          <button type="submit" className="primary-button" disabled={busy} data-testid="reject-confirm">{busy ? "Working…" : "Confirm rejection"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminListingsPage({ user }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState("");

  const canReview = ["SUPER_ADMIN", "OPERATIONS_ADMIN"].includes(user.role);

  const load = async (targetPage = 1) => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (filter) params.set("approval_status", filter);
      if (search) params.set("search", search);
      params.set("page", targetPage); params.set("page_size", 25);
      const res = await client.get(`/admin/listings?${params.toString()}`);
      setItems(res.data.items || []); setCounts(res.data.counts || {});
      setTotal(res.data.total || 0); setPage(res.data.page || 1); setTotalPages(res.data.total_pages || 1);
    } catch (err) { setError(extractError(err, "Unable to load listings.")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id) => {
    setMessage("");
    try { const r = await client.get(`/admin/listings/${id}`); setDetail(r.data); }
    catch (err) { setError(extractError(err)); }
  };

  const doAction = async (action, payload) => {
    await client.post(`/admin/listings/${detail.id}/${action}`, payload || {});
    setMessage(`Listing ${action}d.`);
    const r = await client.get(`/admin/listings/${detail.id}`);
    setDetail(r.data);
    load(page);
  };

  if (detail) {
    return (
      <div className="admin-company-detail" data-testid="admin-listing-detail">
        <button type="button" className="link-button back" data-testid="admin-listing-back" onClick={() => setDetail(null)}><ChevronLeft size={14} /> Back to listings</button>
        <header className="detail-header">
          <div>
            <p className="eyebrow">{detail.listing_type}</p>
            <h2>{detail.title}</h2>
            <p className="muted">{detail.company_name}{detail.brand_name ? ` · ${detail.brand_name}` : ""} — {detail.city_name}, {detail.state_name}</p>
          </div>
          <div className="chip-stack">
            <span className={`status-chip status-${detail.approval_status.toLowerCase()}`}>{APPROVAL_LABEL[detail.approval_status]}</span>
            <span className={`status-chip status-${detail.operational_status.toLowerCase()}`}>{OPERATIONAL_LABEL[detail.operational_status]}</span>
          </div>
        </header>
        {message && <div className="form-success" data-testid="admin-listing-message">{message}</div>}
        {error && <div className="form-error">{error}</div>}
        {detail.rejection_reason && <div className="review-callout rejected"><p className="eyebrow">REJECTION REASON</p><p>{detail.rejection_reason}</p></div>}
        {detail.admin_review_notes && <div className="review-callout internal"><p className="eyebrow">INTERNAL NOTES</p><p>{detail.admin_review_notes}</p></div>}
        {canReview && (
          <div className="detail-actions">
            <button type="button" className="primary-button" data-testid="admin-listing-approve" disabled={detail.approval_status !== "PENDING_REVIEW"} onClick={() => doAction("approve")}><CheckCircle2 size={14} /> Approve</button>
            <button type="button" className="danger-button-solid" data-testid="admin-listing-reject" disabled={detail.approval_status !== "PENDING_REVIEW"} onClick={() => setDialog("reject")}><XCircle size={14} /> Reject</button>
            <button type="button" className="ghost-button" data-testid="admin-listing-pause" disabled={detail.operational_status !== "ACTIVE"} onClick={() => doAction("pause")}><Pause size={14} /> Pause</button>
            <button type="button" className="ghost-button" data-testid="admin-listing-archive" disabled={detail.operational_status === "ARCHIVED"} onClick={() => doAction("archive")}><Archive size={14} /> Archive</button>
          </div>
        )}
        <section className="detail-grid">
          <article><p className="eyebrow">CATEGORY</p><p>{detail.category_name}{detail.subcategory_name ? ` · ${detail.subcategory_name}` : ""}</p></article>
          <article><p className="eyebrow">QUANTITY</p><p>{detail.quantity ?? "—"} {detail.quantity_unit || ""}</p></article>
          <article><p className="eyebrow">INDICATIVE VALUE</p><p>₹{detail.indicative_value.toLocaleString("en-IN")}</p></article>
          <article><p className="eyebrow">MIN TRADE VALUE</p><p>{detail.minimum_trade_value ? `₹${detail.minimum_trade_value.toLocaleString("en-IN")}` : "—"}</p></article>
          <article className="wide"><p className="eyebrow">SHORT</p><p>{detail.short_description}</p></article>
          <article className="wide"><p className="eyebrow">DETAILS</p><p>{detail.detailed_description}</p></article>
        </section>
        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">AUDIT LOG</p><h3>Listing history</h3></div></div>
          <ul className="audit-list" data-testid="admin-listing-audit">
            {(detail.audit_log || []).length === 0 && <li className="muted small">No audit entries.</li>}
            {(detail.audit_log || []).map((a) => (
              <li key={a.id}>
                <div className="audit-status"><span className="chip chip-info">{a.event}</span><small>{a.previous_state || "—"} → {a.new_state || "—"}</small></div>
                <div className="audit-meta"><small>{new Date(a.created_at).toLocaleString()}</small><small>{a.actor_email || "system"} ({a.actor_role || "—"})</small></div>
                {a.reason && <p className="audit-reason">{a.reason}</p>}
              </li>
            ))}
          </ul>
        </section>
        {dialog === "reject" && <RejectDialog onClose={() => setDialog(null)} onSubmit={(p) => doAction("reject", p)} />}
      </div>
    );
  }

  return (
    <div className="admin-listings-page" data-testid="admin-listings-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="admin-listing-tabs">
          {FILTER_OPTIONS.map((o) => (
            <button key={o.value || "all"} type="button" data-testid={`admin-filter-${o.value || "all"}`} className={filter === o.value ? "active" : ""} onClick={() => setFilter(o.value)}>
              {o.label}{o.value && counts[o.value] > 0 && <span className="count">{counts[o.value]}</span>}
            </button>
          ))}
        </div>
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); load(1); }}>
          <input placeholder="Search title" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-listing-search" />
          <button type="submit" className="ghost-button">Search</button>
        </form>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="admin-listings-table">
          <thead><tr><th>Title</th><th>Company</th><th>Type</th><th>City</th><th>Value</th><th>Approval</th><th>Operational</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="empty-row">No listings in this view.</td></tr>}
            {items.map((l) => (
              <tr key={l.id} data-testid={`admin-listing-row-${l.id}`}>
                <td><strong>{l.title}</strong></td>
                <td>{l.company_name}</td>
                <td>{l.listing_type}</td>
                <td>{l.city_id?.slice(0, 8) || "—"}</td>
                <td>₹{l.indicative_value.toLocaleString("en-IN")}</td>
                <td><span className={`status-chip status-${l.approval_status.toLowerCase()}`}>{APPROVAL_LABEL[l.approval_status]}</span></td>
                <td><span className={`status-chip status-${l.operational_status.toLowerCase()}`}>{OPERATIONAL_LABEL[l.operational_status]}</span></td>
                <td><button type="button" className="link-button" data-testid={`admin-open-listing-${l.id}`} onClick={() => openDetail(l.id)}>Review →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">{total} listings</p>
    </div>
  );
}
